/* eslint-disable no-restricted-syntax */
/* eslint-disable no-param-reassign */
import { getPathValue, setPathValue, sortBySpecificArray } from './common'
import { groupBy } from './group'
import { convertLine } from './merge'
import { updateUrlWithParams } from './url'

function matchValue(sourceValue, targetValue, like) {
  try {
    const v1 = `${sourceValue}`
    const v2 = `${targetValue}`
    if (like) {
      return v2.toLowerCase().includes(v1.toLowerCase())
    }
    return v2.toLowerCase() === v1.toLowerCase()
  } catch (e) /* istanbul ignore next */ {
    console.error(e)
    return false
  }
}

function matchValues(sourceValue, targetValues, like) {
  return targetValues.some((targetValue) => matchValue(sourceValue, targetValue, like))
}

function matchInUniKeys(item, key, values, like) {
  const [prePart, postPart] = key.split('[x]')
  const currentObjs = getPathValue(item, prePart)
  let targetValues
  if (postPart) {
    // git rid of the first ., e.g. postPart should be '.name' from displayNames[x].name
    const fixedPostPart = postPart.slice(1)
    targetValues = currentObjs.map((obj) => getPathValue(obj, fixedPostPart))
  } else {
    targetValues = currentObjs
  }
  return values.some((value) => matchValues(value, targetValues, like))
}

function matchInUniKey(item, key, values, like) {
  const currentValue = getPathValue(item, key)
  return values.some((value) => matchValue(value, currentValue, like))
}

function matchByKey(item, key, values, like) {
  return key.includes('[x]') ? matchInUniKeys(item, key, values, like) : matchInUniKey(item, key, values, like)
}

function match(item, condition) {
  const { key, values, like } = condition
  // key might be aaa.bbb||ccc.dddd||eee.ffff
  if (key.includes('||')) {
    const keys = key.split('||')
    if (Array.isArray(like)) {
      return keys.some((k, idx) => matchByKey(item, k, values, like[idx]))
    }
    return keys.some((k) => matchByKey(item, k, values, like))
  }
  return matchByKey(item, key, values, like)
}

function filter(fundItems, rule) {
  let ret = fundItems
  try {
    const { conditions, reducingConditions } = rule.parameters.filter
    conditions.forEach((condition) => {
      ret = ret.filter((item) => match(item, condition))
    })
    // twice reduce
    if (Array.isArray(reducingConditions) && reducingConditions.length > 0) {
      reducingConditions.forEach((condition) => {
        const { path, ...others } = condition
        ret = ret.map((item) => {
          const subItems = getPathValue(item, path)
          const reducedSubItems = subItems.filter((subItem) => match(subItem, others))
          if (reducedSubItems.length === 0) {
            return item
          }
          return setPathValue(item, path, reducedSubItems)
        })
      })
    }
    return ret
  } catch (e) /* istanbul ignore next */ {
    console.error(e)
    return ret
  }
}

function abstractOptions(fundItems, key) {
  try {
    const ret = groupBy(fundItems, key)
    return ret.map((items) => getPathValue(items[0], key, ''))
  } catch (e) /* istanbul ignore next */ {
    console.error(e)
    return []
  }
}

function getInitialFilterData(fundItems, rule, terms) {
  try {
    const filterRule = rule.filter
    return convertLine(filterRule, fundItems, rule, terms)
  } catch (e) /* istanbul ignore next */ {
    console.error(e)
    return null
  }
}

function getLabelsToValues(fundItems, labelValuePairs) {
  const ret = {}
  labelValuePairs.forEach((pair) => {
    if (pair.label && pair.value) {
      const labels = abstractOptions(fundItems, pair.label)
      const values = abstractOptions(fundItems, pair.value)
      const len = Math.min(labels.length, values.length)
      const ltv = {}
      if (len > 0) {
        labels.forEach((label, idx) => {
          ltv[label] = values[idx]
        })
      }
      ret[pair.key] = ltv
    } else {
      ret[pair.key] = {}
    }
  })
  return ret
}

// paramObj = {key1: [value1, value2]}
// keys = [key1, key2]}
function updateFilterFromParams(filterData, paramObj, keys = []) {
  if (keys.length === 0) {
    return filterData
  }
  return keys.reduce((acc, key) => {
    const { blocks, ...others } = acc
    return {
      blocks: blocks.map((block) => {
        const { elements, ...others1 } = block
        return {
          elements: elements.map((elem) => {
            if (elem.key === key && paramObj[key]) {
              if (elem.type === 'input') {
                return Object.assign(elem, {
                  value: paramObj[key].trim()
                })
              }
              const tempLabelArray = Array.isArray(paramObj[key]) ? paramObj[key] : [paramObj[key]]
              if (elem.type === 'check') {
                const { options, ...others2 } = elem
                return {
                  options: options.map((option) => {
                    if (tempLabelArray.includes(option.label)) {
                      return Object.assign(option, { checked: true })
                    }
                    return option
                  }),
                  ...others2
                }
              }
              if (elem.type === 'select') {
                const { value, options, ...others3 } = elem
                return {
                  options: options.map((option) => {
                    if (tempLabelArray.includes(option.label)) {
                      return Object.assign(option, { checked: true })
                    }
                    return option
                  }),
                  value: tempLabelArray,
                  ...others3
                }
              }
            }
            return elem
          }),
          ...others1
        }
      }),
      ...others
    }
  }, filterData)
}

function getFilterUsingKeys(data) {
  const { blocks } = data
  return blocks
    .flatMap((block) => block.elements)
    .filter((elem) => {
      if (elem.type === 'input') {
        return elem.value
      }
      if (elem.type === 'check') {
        return elem.options.some((option) => option.checked === true)
      }
      if (elem.type === 'select') {
        return elem.value.length > 0
      }
      return false
    })
    .map((elem) => elem.key)
}

function produceCondition(elem) {
  const { key, type } = elem
  let { value: values } = elem
  let like = false
  if (key) {
    if (type === 'check') {
      const { options } = elem
      values = options.filter((option) => option.checked === true).map((option) => option.label)
    } else if (type === 'input') {
      like = true
    }
    if (Array.isArray(values)) {
      if (values.length > 0) {
        return { key, values, like }
      }
    } else if (values) {
      const { wholeMatchKeys } = elem
      if (Array.isArray(wholeMatchKeys) && wholeMatchKeys.length > 0) {
        const keys = key.split('||')
        const likes = keys.map((k) => !wholeMatchKeys.includes(k))
        return { key, values: [values], like: likes }
      }
    } else {
      return { key, values: [values], like }
    }
  }
  return null
}

// text is '富達基金－新興亞洲基金'
// words is ['富達', '基金']
// return should be ['富達', '基金', '新興亞洲']
function splitWords(text, words = [], dividing = ' ') {
  if (typeof text !== 'string') {
    return []
  }
  let tempText = text
  const tempWords = words.concat(dividing)
  if (Array.isArray(tempWords) && tempWords.length > 0) {
    words.forEach((word) => {
      tempText = tempText.replaceAll(word, `${dividing}${word}${dividing}`)
    })
    const wordsSet = new Set(
      tempText.split(dividing).filter((word) => {
        if (dividing === ' ') {
          return !!word.trim() && word !== dividing
        }
        return !!word && word !== dividing
      })
    )
    return Array.from(wordsSet.values())
  }
  return [text]
}

const mergeSplittingWords = (filterRule, splittingWords) => {
  const { blocks } = filterRule
  return {
    ...filterRule,
    blocks: blocks.map((block) => {
      const { elements } = block
      return {
        ...block,
        elements: elements.map((elem) => {
          const { splittingWords: words } = elem
          return {
            ...elem,
            ...(words == null ? {} : { splittingWords })
          }
        })
      }
    })
  }
}

function reduceItemsByFilter(fundItems, filterData) {
  const { blocks, twiceReducers } = filterData
  const conditions = []
  blocks.forEach((block) => {
    block.elements.forEach((elem) => {
      const { type, value, splittingWords, dividingWord } = elem
      if (type === 'input' && Array.isArray(splittingWords) && splittingWords?.length > 0) {
        splitWords(value, splittingWords, dividingWord)
          .map((text) => {
            return { ...elem, value: text }
          })
          .forEach((exElem) => {
            const cond = produceCondition(exElem)
            if (cond) {
              conditions.push(cond)
            }
          })
      } else {
        const cond = produceCondition(elem)
        if (cond) {
          conditions.push(cond)
        }
      }
    })
  })
  // twice reduce
  const reducingConditions = []
  if (twiceReducers) {
    twiceReducers.forEach((condition) => {
      const { id, path, ...others } = condition
      const matchedItem = blocks.flatMap((block) => block.elements).find((elem) => elem.reducerId === id)
      if (matchedItem && matchedItem.value) {
        const newCond = produceCondition({ ...others, value: matchedItem.value })
        reducingConditions.push({ ...newCond, path })
      }
    })
  }
  return filter(fundItems, { parameters: { filter: { conditions, reducingConditions } } })
}

function updateFilterFromAction(oldData, newAction) {
  const { type, id, label, value, clearAll } = newAction
  const { blocks } = oldData
  const newBlocks = blocks

  if (!clearAll) {
    newBlocks.forEach((item) => {
      item.elements.forEach((ele) => {
        if (ele.key === id) {
          if (type === 'select' || type === 'input') {
            ele.value = value
          }
          if (type === 'check') {
            ele.options.forEach((option) => {
              if (option.label === label) {
                option.checked = value
              }
            })
          }
        }
      })
    })
  } else {
    newBlocks.forEach((item) => {
      item.elements.forEach((ele) => {
        if (ele.type === 'input') {
          ele.value = ''
        }
        if (ele.type === 'select') {
          ele.value = []
        }
        if (ele.type === 'check') {
          ele.options.forEach((option) => {
            option.checked = false
          })
        }
      })
    })
  }
  return Object.assign(oldData, { blocks: newBlocks })
}

function updateFilterToUrl(data) {
  const { blocks } = data
  let selectedItemObj = {}
  blocks.forEach((block) => {
    block.elements.forEach((ele) => {
      if (ele.type === 'input') {
        selectedItemObj = { ...selectedItemObj, [`${ele.key}`]: ele.value }
      }
      if (ele.type === 'check') {
        const tempLabelArray = []
        ele.options.forEach((option) => {
          if (option.checked) {
            tempLabelArray.push(option.label)
          }
        })
        selectedItemObj = { ...selectedItemObj, [`${ele.key}`]: tempLabelArray }
      }
      if (ele.type === 'select') {
        selectedItemObj = { ...selectedItemObj, [`${ele.key}`]: ele.value }
      }
    })
  })
  return updateUrlWithParams({ query: selectedItemObj }, { encode: true })
}

function produceCheckOptions(fundItems, key, order = [], extraClasses = []) {
  const ret = abstractOptions(fundItems, key)
  return sortBySpecificArray(ret, order).map((value) => {
    return {
      label: value,
      checked: false,
      extraClasses
    }
  })
}

function produceSelectOptions(fundItems, key, order) {
  const ret = abstractOptions(fundItems, key).filter((value) => value)
  const retOrdered = order ? sortBySpecificArray(ret, order) : ret.sort((a, b) => a.localeCompare(b))
  return retOrdered.map((value) => {
    return {
      value,
      label: value,
      checked: false
    }
  })
}

function syncFilterAction(action, filterData) {
  const { type } = action
  const { delegation } = filterData
  if (delegation && delegation[action.id]) {
    const { lead } = delegation[action.id]
    if (lead) {
      if (type === 'check') {
        let { id, label } = action
        for (const block of filterData.blocks) {
          for (const element of block.elements) {
            if (element.key === action.id) {
              id = lead.key
              label = lead.map[action.label]
            }
          }
        }
        return Object.assign(action, {
          id,
          label
        })
      }
    }
  }
  return action
}

function syncFilterData(filterData) {
  const { delegation, blocks } = filterData
  const overwriteData = {}
  if (delegation) {
    blocks.forEach((block) => {
      block.elements.forEach((element) => {
        if (delegation[element.key]?.delegated) {
          const { delegated } = delegation[element.key]
          if (element.type === 'check') {
            overwriteData[delegated.key] = element.options.map((option) => {
              return {
                ...option,
                label: delegated.map[option.label]
              }
            })
          }
        }
      })
    })
    return {
      ...filterData,
      blocks: blocks.map((block) => {
        const { elements } = block
        return {
          ...block,
          elements: elements.map((elem) => {
            if (elem.type === 'check' && overwriteData[elem.key]) {
              return {
                ...elem,
                options: elem.options.map((option, idx) => {
                  return {
                    ...option,
                    ...overwriteData[elem.key][idx],
                    extraClasses: option.extraClasses
                  }
                })
              }
            }
            return elem
          })
        }
      })
    }
  }
  return filterData
}

function correctAction(action) {
  if (action.type === 'input') {
    return Object.assign(action, {
      value: action.value.trim()
    })
  }
  return action
}

function produceAPIParams(labelsToValues, filterData) {
  if (labelsToValues && filterData) {
    const ret = {}
    const { blocks, labelValuePairs } = filterData
    blocks.forEach((block) => {
      const { elements } = block
      elements.forEach((elem) => {
        const { key, value } = elem
        const pair = labelsToValues[key]
        if (pair) {
          let tmpValue = value
          if (elem.type === 'check') {
            tmpValue = elem.options.filter((opt) => opt.checked).map((opt) => opt.label)
          }
          let pairedValues = Array.isArray(tmpValue) ? tmpValue : [tmpValue]
          if (elem.type === 'select' || elem.type === 'check') {
            pairedValues = tmpValue.map((val) => pair[val])
          }
          const found = labelValuePairs.find((p) => p.key === key)
          if (found) {
            ret[found.apiKey] = pairedValues
          }
        }
      })
    })
    return ret
  }
  return null
}

export {
  matchValue,
  matchValues,
  matchInUniKey,
  matchInUniKeys,
  matchByKey,
  match,
  filter,
  abstractOptions,
  getInitialFilterData,
  getFilterUsingKeys,
  splitWords,
  mergeSplittingWords,
  reduceItemsByFilter,
  updateFilterFromParams,
  updateFilterFromAction,
  updateFilterToUrl,
  produceCondition,
  produceCheckOptions,
  produceSelectOptions,
  syncFilterAction,
  syncFilterData,
  correctAction,
  getLabelsToValues,
  produceAPIParams
}
